import moment from 'moment';

export const getTipoVeiculoLabel = (tipo) => {
  if (tipo === 'ELETROPONTES') return 'Eletropontes';
  if (tipo === 'PARTICULAR') return 'Particular';
  if (tipo === 'ALUGADO') return 'Alugado';
  return tipo || '';
};

export const formatNumber = (value) => {
  const numero = Number(value);
  if (!value || !Number.isFinite(numero)) return 'N/A';
  return new Intl.NumberFormat('pt-BR').format(numero);
};

export const isLimpezaVeicular = (tipo) =>
  tipo === 'LIMPEZA';

export const isIpva = (tipo) => tipo === 'IPVA';

export const isTermoDeslocamento = (tipo) => tipo === 'TERMO_DESLOCAMENTO';

export const isRastreador = (tipo) => tipo === 'RASTREADOR';

export const isCamera = (tipo) => tipo === 'CAMERA';

export const isDespachante = (tipo) => tipo === 'DESPACHANTE';

export const isTipoComCodigo = (tipo) => isRastreador(tipo) || isCamera(tipo);

/** Tipos que usam o campo genérico `valor` (obrigatório). */
export const isTipoComValor = (tipo) => isTipoComCodigo(tipo) || isDespachante(tipo);

export const isHistoricoReadOnly = (row) =>
  Boolean(row?.notaId) || row?.readOnly === true;

export const tipoHistoricoNome = (tipoHistorico) => {
  switch (tipoHistorico) {
    case 'MANUTENCAO':
      return 'Manutenção';
    case 'LIMPEZA':
      return 'Limpeza Veicular';
    case 'PECAS':
      return 'Peças';
    case 'MULTA':
      return 'Multa';
    case 'AUTUACAO':
      return 'Autuação';
    case 'REPASSE_COLABORADOR':
      return 'Repasse';
    case 'VENDA_TROCA':
      return 'Venda/Troca';
    case 'IPVA':
      return 'IPVA';
    case 'TERMO_DESLOCAMENTO':
      return 'Termo de uso particular';
    case 'RASTREADOR':
      return 'Rastreador';
    case 'CAMERA':
      return 'Câmera';
    case 'DESPACHANTE':
      return 'Despachante / Transferência';
    default:
      return tipoHistorico;
  }
};

export const getTipoHistoricoLabel = (row) => {
  const fromApi = row?.tipoHistoricoNome && String(row.tipoHistoricoNome).trim();
  if (fromApi) return fromApi;
  return tipoHistoricoNome(row?.tipoHistorico);
};

const ANO_MIN = 1900;
const ANO_MAX = 2100;

export const parseAnoIpva = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') {
    if (raw >= ANO_MIN && raw <= ANO_MAX) return raw;
    const fromTs = moment(raw);
    if (!fromTs.isValid()) return null;
    const year = fromTs.year();
    return year >= ANO_MIN && year <= ANO_MAX ? year : null;
  }
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length !== 4) return null;
  const year = Number(digits);
  return year >= ANO_MIN && year <= ANO_MAX ? year : null;
};

export const toAnoPickerValue = (ano) => {
  if (ano == null || ano === '') return '';
  const year = parseAnoIpva(ano);
  if (year) return moment(`${year}-01-01`).startOf('year').valueOf();
  return ano;
};

export const getAnoExercicio = (row) => {
  const fromField = parseAnoIpva(row?.ipvaAno ?? row?.ano);
  if (fromField) return fromField;
  if (row?.dataHistorico == null || row.dataHistorico === '') return null;
  const parsed = moment(row.dataHistorico);
  if (!parsed.isValid()) return null;
  const year = parsed.year();
  return year >= ANO_MIN && year <= ANO_MAX ? year : null;
};

export const getValorMensalIpva = (valorIpva) => {
  if (valorIpva == null || valorIpva === '') return '';
  const anual = Number(valorIpva);
  if (!Number.isFinite(anual)) return '';
  return Math.round((anual / 12) * 100) / 100;
};

export const getValorAnualIpva = (row) => {
  if (!row || typeof row !== 'object') return null;
  if (row.valorIpva != null && row.valorIpva !== '') {
    const anual = Number(row.valorIpva);
    if (Number.isFinite(anual)) return anual;
  }
  if (row.valorMensal != null && row.valorMensal !== '') {
    const mensal = Number(row.valorMensal);
    if (Number.isFinite(mensal)) return Math.round(mensal * 12 * 100) / 100;
  }
  return null;
};

export const getHistoricoValor = (row) => {
  if (!row) return null;
  if (isTermoDeslocamento(row.tipoHistorico)) return null;
  if (isTipoComValor(row.tipoHistorico)) return row.valor ?? null;
  if (isIpva(row.tipoHistorico)) return getValorAnualIpva(row);
  if (row.tipoHistorico === 'PECAS') return row.valorPecas;
  if (row.tipoHistorico === 'VENDA_TROCA') return row.valorRecebido;
  if (row.tipoHistorico === 'MULTA' || row.tipoHistorico === 'AUTUACAO') {
    return row.multaValor;
  }
  if (row.tipoHistorico === 'MANUTENCAO' || isLimpezaVeicular(row.tipoHistorico)) {
    return row.valorManutencao;
  }
  return row.valorIpva ?? row.valorManutencao ?? row.valorPecas
    ?? row.multaValor ?? row.valorRecebido ?? row.valor ?? null;
};

export const formatHistoricoValor = (value) => {
  if (value == null || value === '') return '—';
  const numero = Number(value);
  if (!Number.isFinite(numero)) return '—';
  return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const getUsuarioNome = (row, fieldId, fieldNome) => {
  if (row?.[fieldNome] && String(row[fieldNome]).trim()) {
    return String(row[fieldNome]).trim();
  }
  const raw = row?.[fieldId];
  if (raw && typeof raw === 'object' && raw.nome) return raw.nome;
  return '';
};

export const getHistoricoDescricao = (row) => {
  if (!row) return '';
  const observacao = row.observacao && String(row.observacao).trim();
  if (observacao) return observacao;
  if (isTermoDeslocamento(row.tipoHistorico)) {
    const colaborador = getUsuarioNome(row, 'usuario', 'usuarioNome');
    const autorizador = getUsuarioNome(row, 'autorizadoPor', 'autorizadoPorNome');
    if (colaborador && autorizador) {
      return `${colaborador} · autorizado por ${autorizador}`;
    }
    if (colaborador) return colaborador;
    if (autorizador) return `Autorizado por ${autorizador}`;
  }
  if (isTipoComCodigo(row.tipoHistorico)) {
    const codigo = row.codigo && String(row.codigo).trim();
    return codigo ? `Código: ${codigo}` : '';
  }
  if (isDespachante(row.tipoHistorico)) {
    const partes = [row.empresa, row.profissionalResponsavel]
      .map((parte) => parte && String(parte).trim())
      .filter(Boolean);
    return partes.join(' · ');
  }
  return '';
};

export const formatHistoricoData = (row) => {
  if (isIpva(row?.tipoHistorico)) {
    const ano = getAnoExercicio(row);
    return ano ? String(ano) : '—';
  }
  if (row?.dataHistorico == null || row.dataHistorico === '') return '—';
  const parsed = moment(row.dataHistorico);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '—';
};

export const hasIpvaDuplicado = (rows, ano, excludeId) => {
  const year = parseAnoIpva(ano);
  if (!year) return false;
  return (rows || []).some((row) => (
    isIpva(row.tipoHistorico)
    && getAnoExercicio(row) === year
    && row.id !== excludeId
  ));
};

const toHistoricoSortKey = (row) => {
  if (!row) return '';
  if (isIpva(row.tipoHistorico)) {
    const ano = getAnoExercicio(row);
    if (ano) return `${ano}-01-01`;
  }
  const raw = row.dataHistorico;
  if (raw == null || raw === '') {
    const ano = getAnoExercicio(row);
    return ano ? `${ano}-01-01` : '';
  }
  if (typeof raw === 'number') {
    const parsed = moment(raw);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : String(raw);
  }
  return String(raw);
};

export const ordernarPorDataHistorico = (a, b) =>
  toHistoricoSortKey(b).localeCompare(toHistoricoSortKey(a));
